import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import { ModalVariant } from '@/components/Modals/types';
import { JiraIssueType } from '@/types/schema';

import useAddTasksFromJiraModal from '../../hooks/useAddTasksFromJiraModal';
import EmptyState from './EmptyState';
import ListItem from './ListItem';
import { headerIconStyles } from './styles';
import { AddTaskFromJiraModalProps } from './types';

function AddTaskFromJiraModal(props: AddTaskFromJiraModalProps) {
  const { t } = useTranslation('Jira');
  const { onClose, provider } = props;
  const { state, methods } = useAddTasksFromJiraModal(props);
  const {
    jiraIssues,
    isSubmitting,
    search,
    debounceSearchStr,
    isLoading,
    isFetching,
    error,
    fetchMore,
    isFetchingMore,
    totalCount,
    selectedFiltersCount,
  } = state;

  const { onSubmit, setSearch, clearSearch, toggle, isItemSelected } = methods;

  const renderContent = useCallback(() => {
    if (isLoading || isFetching) {
      return <Loader />;
    }

    if (!jiraIssues?.length) {
      return <EmptyState clearSearch={clearSearch} error={error} search={search} />;
    }

    if (jiraIssues?.length) {
      return (
        <>
          {debounceSearchStr.trim().length ? (
            <Stack alignItems="center" className="w-full px-32">
              <Typography className="w-full border-b border-border px-8 py-16" variant="label-01">
                {t('Jira:Tasks.TaskFound', { count: totalCount })}
              </Typography>
            </Stack>
          ) : null}
          <ContentListModal.Body
            className="flex-1"
            isFetchingMore={isFetchingMore}
            itemRenderer={(index: number) => {
              const jiraIssue = jiraIssues[index] as JiraIssueType;

              return (
                <ListItem
                  key={jiraIssue.id}
                  isFirstItem={index === 0}
                  jiraIssue={jiraIssue}
                  selected={isItemSelected(jiraIssue.id)}
                  onSelect={toggle}
                />
              );
            }}
          />
        </>
      );
    }

    return null;
  }, [
    clearSearch,
    debounceSearchStr,
    error,
    isFetching,
    isFetchingMore,
    isItemSelected,
    isLoading,
    jiraIssues,
    search,
    t,
    toggle,
    totalCount,
  ]);

  return (
    <ContentListModal.Main
      action={{
        text: t('Jira:Tasks.AddTasks'),
        onSubmit,
        disabled: !selectedFiltersCount,
        TertiaryAction: (
          <Typography color="text-text-placeholder" variant="label-02">
            {t('Jira:IssuesSelected', { count: selectedFiltersCount })}
          </Typography>
        ),
        isSubmitting,
      }}
      fetchMore={fetchMore}
      header={{
        title: t('Jira:Tasks.AddTaskFromProvider', { providerName: provider.name }),
        description: t('Jira:Tasks.AddTaskFromProviderDescription', {
          providerName: provider.name,
        }),
        avatar: {
          src: provider.logo.url,
          className: headerIconStyles,
        },
      }}
      items={jiraIssues}
      searchBar={{
        value: search,
        onChange: setSearch,
        placeholder: t('Jira:JQLSearch'),
      }}
      variant={ModalVariant.ExtraWide}
      onClose={onClose}>
      {renderContent()}
    </ContentListModal.Main>
  );
}

export default AddTaskFromJiraModal;
